import { useState, useEffect } from 'react';

/**
 * AI Visibility Scan hero: a 4-frame storyboard that cycles through the core
 * loop (measure → understand → fix → re-measure), matching the design language
 * of the "The Loop" section on pagecoder.ai.
 *
 * JS-driven: it auto-advances one frame every 4s. Hovering a frame pauses the
 * loop and highlights that frame; on hover-out the loop resumes from the frame
 * you last hovered. Respects prefers-reduced-motion (no auto-advance).
 */
const QUESTION = '“best tool for my problem?”';
const STEP_MS = 4000;

const FRAMES = [
  {
    phase: 'f1',
    tag: '01 · Ask AI',
    caption: 'A buyer asks AI. It names competitors, not you.',
    body: (
      <div className="space-y-1.5">
        <div className="text-sm text-text-secondary">{QUESTION}</div>
        <div className="flex flex-wrap gap-1">
          {['Competitor A', 'Competitor B', 'Competitor C'].map((c) => (
            <span key={c} className="font-mono text-sm px-1.5 py-0.5 rounded bg-warm-dark-2 text-text-secondary">{c}</span>
          ))}
        </div>
        <div className="font-mono text-sm text-[#E0574A]">✕ you: absent</div>
      </div>
    ),
  },
  {
    phase: 'f2',
    tag: '02 · Diagnose',
    caption: 'The scan measures why AI can’t read or cite you.',
    body: (
      <div className="space-y-1.5">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-2xl text-cyan">62</span>
          <span className="font-mono text-sm text-text-secondary">/100 readiness</span>
        </div>
        {[['Structure', 82], ['Entity', 55], ['Off-page', 30]].map(([l, v]) => (
          <div key={l as string} className="flex items-center gap-2">
            <span className="w-16 text-sm text-text-secondary">{l}</span>
            <div className="flex-1 h-1.5 rounded bg-warm-dark-2 overflow-hidden">
              <div className="h-full rounded bg-cyan" style={{ width: `${v}%` }} />
            </div>
          </div>
        ))}
      </div>
    ),
  },
  {
    phase: 'f3',
    tag: '03 · Fix it',
    caption: 'You get an honest, free action plan: content and footprint.',
    body: (
      <div className="space-y-1.5">
        {['Publish answers to real questions', 'Add sameAs + knowledge base', 'Build off-site mentions'].map((t, i) => (
          <div key={t} className="flex items-center gap-2 text-sm text-text-secondary">
            <span className="w-4 h-4 shrink-0 rounded-full bg-cyan text-warm-dark font-mono text-sm flex items-center justify-center">{i + 1}</span>
            {t}
          </div>
        ))}
      </div>
    ),
  },
  {
    phase: 'f4',
    tag: '04 · Cited ✓',
    caption: 'Ask AI again, now it names you.',
    body: (
      <div className="space-y-1.5">
        <div className="text-sm text-text-secondary">{QUESTION}</div>
        <div className="text-sm text-text-primary">
          “…a strong option is <mark className="bg-cyan/25 text-cyan rounded px-0.5">You</mark>, which…”
        </div>
        <div className="font-mono text-sm text-cyan">✓ you: mentioned</div>
      </div>
    ),
  },
];

export default function HeroLoop(): JSX.Element {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  // Auto-advance unless paused (hover) or reduced-motion. Resuming after a hover
  // continues from the currently-active frame (the one last hovered).
  useEffect(() => {
    if (paused || reduced) return;
    const id = window.setInterval(() => setActive((a) => (a + 1) % FRAMES.length), STEP_MS);
    return () => window.clearInterval(id);
  }, [paused, reduced]);

  return (
    <div className="hero-loop w-full max-w-5xl mx-auto my-10 md:my-14">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-4 relative">
        {FRAMES.map((f, i) => {
          const lit = active === i;
          return (
            <div
              key={f.phase}
              onMouseEnter={() => { setActive(i); setPaused(true); }}
              onMouseLeave={() => setPaused(false)}
              className={`rounded-xl border p-4 flex flex-col cursor-default transition-all duration-500 ${
                lit ? 'border-cyan/40 bg-warm-dark-2/40 opacity-100' : 'border-warm-dark-2 bg-warm-dark-2/20 opacity-40'
              }`}
            >
              <div className="font-mono text-sm uppercase tracking-widest text-cyan/80 mb-2">{f.tag}</div>
              <div className="flex-1">{f.body}</div>
              <div className="text-sm text-text-secondary mt-3 pt-3 border-t border-warm-dark-2">{f.caption}</div>
            </div>
          );
        })}
      </div>

      {/* Loop-back line: from frame 4 → frame 1 */}
      <div className={`mt-4 flex items-center gap-2 justify-center font-mono text-sm transition-colors duration-500 ${active === FRAMES.length - 1 ? 'text-cyan' : 'text-cyan/50'}`}>
        <span>↻ measure, fix, re-measure. The loop you own</span>
      </div>
    </div>
  );
}
